import { useState } from 'react'
import { supabase } from '@/shared/lib/supabase'
import { useLocalizedNavigate } from '@/shared/hooks'
import {
  storePendingFiles,
  getPendingFiles,
  clearPendingFiles,
} from '@/shared/lib/file-storage'

// Storage key for pending pre-scoping data
const PRECADRAGE_STORAGE_KEY = 'heynicolai_pending_precadrage'

// File upload constraints
export const FILE_CONSTRAINTS = {
  maxSizePerFile: 10 * 1024 * 1024, // 10MB
  maxTotalSize: 50 * 1024 * 1024, // 50MB
  allowedTypes: [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/zip',
    'application/x-zip-compressed',
  ],
  allowedExtensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf', '.doc', '.docx', '.zip'],
}

export interface PrecadrageFormData {
  projectName: string
  description: string
  objective: string
  targetUsers: string
  existingAssets: 'starting_from_scratch' | 'existing_codebase' | 'existing_designs' | 'other'
  constraints: string
  inspirations: string
}

export interface PrecadrageSubmitData extends PrecadrageFormData {
  attachments: File[]
}

/**
 * Stores pre-scoping form data in sessionStorage
 */
export function storePrecadrageData(data: PrecadrageFormData): void {
  sessionStorage.setItem(PRECADRAGE_STORAGE_KEY, JSON.stringify(data))
}

/**
 * Retrieves pending pre-scoping data from sessionStorage
 */
export function getPendingPrecadrageData(): PrecadrageFormData | null {
  const stored = sessionStorage.getItem(PRECADRAGE_STORAGE_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored) as PrecadrageFormData
  } catch {
    return null
  }
}

/**
 * Clears pending pre-scoping data from sessionStorage
 */
export function clearPrecadrageData(): void {
  sessionStorage.removeItem(PRECADRAGE_STORAGE_KEY)
}

/**
 * Validate a file against constraints
 */
export function validateFile(file: File): { valid: boolean; error?: string } {
  // Check file size
  if (file.size > FILE_CONSTRAINTS.maxSizePerFile) {
    return { valid: false, error: 'fileTooLarge' }
  }

  // Check file type
  const isValidType = FILE_CONSTRAINTS.allowedTypes.includes(file.type)
  const extension = '.' + file.name.split('.').pop()?.toLowerCase()
  const isValidExtension = FILE_CONSTRAINTS.allowedExtensions.includes(extension)

  if (!isValidType && !isValidExtension) {
    return { valid: false, error: 'fileTypeNotAllowed' }
  }

  return { valid: true }
}

/**
 * Validate total size of all files
 */
export function validateTotalSize(files: File[]): boolean {
  const totalSize = files.reduce((sum, file) => sum + file.size, 0)
  return totalSize <= FILE_CONSTRAINTS.maxTotalSize
}

/**
 * Hook for submitting pre-scoping form
 * Stores data and files, then redirects to auth
 */
export function useSubmitPrecadrage() {
  const navigate = useLocalizedNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (data: PrecadrageSubmitData) => {
    setIsSubmitting(true)
    setError(null)

    try {
      // Store form data in sessionStorage (without attachments)
      const { attachments, ...formData } = data
      storePrecadrageData(formData)

      // Store files in IndexedDB for retrieval after auth
      if (attachments.length > 0) {
        await storePendingFiles(attachments)
      }

      // Redirect to auth with a flag indicating we're coming from precadrage
      navigate('/?from=precadrage')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      setIsSubmitting(false)
    }
  }

  return { submit, isSubmitting, error }
}

/**
 * Upload files to Supabase Storage
 */
async function uploadProjectAttachments(
  projectId: string,
  files: File[]
): Promise<{ uploaded: number; failed: number }> {
  let uploaded = 0
  let failed = 0

  for (const file of files) {
    const filePath = `${projectId}/${Date.now()}-${file.name}`

    const { error } = await supabase.storage
      .from('project-attachments')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      })

    if (error) {
      console.error(`Failed to upload ${file.name}:`, error)
      failed++
    } else {
      uploaded++
    }
  }

  return { uploaded, failed }
}

/**
 * Creates team and project from pending pre-scoping data
 * Called after successful authentication
 */
export async function createProjectFromPrecadrage(): Promise<{
  success: boolean
  projectShortId?: string
  error?: string
}> {
  const data = getPendingPrecadrageData()

  if (!data) {
    return { success: true } // No pending data, nothing to do
  }

  try {
    // Get current user's organization
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('organization_id')
      .single()

    if (userError || !userData?.organization_id) {
      console.error('Failed to get user organization:', userError)
      return { success: false, error: 'Failed to get user organization' }
    }

    const organizationId = userData.organization_id

    // Create team (pending until payment is made)
    const { data: teamData, error: teamError } = await supabase
      .from('teams')
      .insert({
        organization_id: organizationId,
        name: data.projectName,
        type: 'project',
        status: 'pending',
      })
      .select('id')
      .single()

    if (teamError || !teamData) {
      console.error('Failed to create team:', teamError)
      return { success: false, error: 'Failed to create team' }
    }

    // Create project with pre-scoping data
    // CADRAGE-V2-001: Create directly in 'pending' status, auto-validated
    // Project transitions to 'scoping' after payment via webhook
    const { data: projectData, error: projectError } = await supabase
      .from('projects')
      .insert({
        team_id: teamData.id,
        name: data.projectName,
        description: data.description,
        status: 'pending',
        is_active: true,
        is_validated: true, // Auto-validated, ready for payment
        objective: data.objective,
        target_users: data.targetUsers,
        existing_assets: data.existingAssets,
        constraints: data.constraints || null,
        inspirations: data.inspirations || null,
      })
      .select('id, short_id')
      .single()

    if (projectError || !projectData) {
      console.error('Failed to create project:', projectError)
      return { success: false, error: 'Failed to create project' }
    }

    // Upload pending attachments from IndexedDB
    const pendingFiles = await getPendingFiles()
    if (pendingFiles.length > 0) {
      await uploadProjectAttachments(projectData.id, pendingFiles)
      await clearPendingFiles()
    }

    // Clear the stored form data
    clearPrecadrageData()

    return { success: true, projectShortId: projectData.short_id }
  } catch (err) {
    console.error('Error creating project from precadrage:', err)
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error',
    }
  }
}
